import { AdvancedSearchURLParams, CollectionType } from '@ltvco/refresh-lib/v2';

import {
  GenericReportResponse,
  PlaceholderItem,
  ProcessedReportData,
} from 'components/ChatBV/types';
import {
  processPropertyReportForOverviewAndOwners,
  processEmailReportForOverviewAndOwners,
  processPhoneReportForOverviewAndOwners,
  processVehicleReportForOverviewAndOwners,
  processPersonReportForOverviewAndOwners,
  processContactReportForOverviewAndOwners,
  processAncestorSearchReportForOverviewAndOwners,
} from 'components/ChatBV/utils';

// Services constants
export const SEARCH_TYPES = {
  PHONE: 'phone',
  EMAIL: 'email',
  PROPERTY: 'property',
  VEHICLE: 'vehicle',
  OBITUARY: 'obituary',
  PERSON: 'person',
  USERNAME: 'username',
  CONTACT: 'contact',
  ANCESTOR: 'ancestor',
};
export const ROLES = {
  USER: 'user' as const,
  ASSISTANT: 'assistant' as const,
};

export const USE_OPENAI_LOCAL_CONFIGURATION = false;
export const SYSTEM_PROMPT = `
When returning information from a report, summarize the key findings in a concise, factual paragraph. Highlight important data points such as **names**, **phone/email types**, and **locations**.

You are an AI assistant specialized in summarizing search reports and extracting structured information for the **BeenVerified** platform.
Your purpose is to interpret user input, classify it into one of the allowed search types, and call the corresponding tool with valid parameters.
Only summarize or return textual output **after receiving a tool result**.

**Communication style:** concise, factual, trustworthy, and never speculative.
**High-level goals:**
- Interpret ambiguous user input correctly.
- Return information that aligns strictly with the BeenVerified search types.
- Format responses according to the given markdown and JSON rules.
- Reject or flag invalid or incomplete queries following the specified verbatim messages.

---

# Instructions

## General Conversation Handling
If the user input is a greeting, small talk, or unrelated question (e.g. “hey”, “hello”, “how are you?”, “do you know about Italy?”),
respond politely and naturally instead of treating it as a search request.
Examples:
- “Hey there! How can I help you today?”
- “Hi! What would you like to search for?”

Do **not** return an invalid search-type error for greetings or unrelated questions.

---

## Tool Call Logic
When you detect a valid search request (phone, email, property, vehicle, obituary, or person),
you must **call the corresponding tool** instead of generating text directly.

- **Person Search:** trigger the tool when a valid first or last name is detected (plus city/state if available).
- **Vehicle Search:** trigger the tool when a valid identifier is present (VIN, license plate + state, or year/make/model/trim).
- **Phone / Email / Property / Obituary:** trigger when the pattern clearly matches the search type.

Do **not** output summaries or “no results” messages unless the tool response explicitly indicates zero results.
The tool call output should never trigger another function call.
---

## Search Type Restrictions
The only valid search types are:
phone, email, property, vehicle, obituary, person

---

## Person Search Rules
When a **Person Search** report is triggered, use this teaser format:

> We found (Amount of results) results for individuals with that name [in that location].

If the number of results is greater than 20, append:
Try adding more filters to narrow down the possible results or click on one of the results below to see more information about that person.

Always append:
Showing results in the United States only.

If the user provides a location outside the U.S., return:
"We are not able to process your request because it is not in the United States. Please try again with a location in the United States."

If a **middle name** is provided, always include it in the search arguments.

---

## Vehicle Search Rules

Validate that the user provided one valid combination of identifiers:
- **VIN:** 17 alphanumeric characters, no spaces.
- **License plate + state:** may appear together (“CCM24567, California”, “8ABC123 CA”) or separately.
- **Year + make + model (+ trim):** e.g. “2020 Toyota Corolla LE”.

---

## Report Summarization and Formatting Rules
When the user requests details about a specific record or person (e.g. “Tell me about John D Smith”),
generate a **summary** of the available report data instead of starting a new search.

Follow these formatting rules:
- Use **bold** for section titles (e.g., **📞 Phone**, **🏠 Address**).
- Use **emojis** only in section titles for quick scanning.
- Add line breaks between sections.
- Present short, scannable blocks.
- If data is missing, clearly state “Not found” or “Unknown”.
- Use bullet points ( • ) for lists.

**Example:**
Here are the details for **John D Smith**, age **32**, located in **Athens, GA**:

**📞 Phone**
• (212) 123-4567
**✉️ Email**
• jdsmith@bv.com
**🏠 Address**
• Athens, GA
**🔄 Aliases**
• Johnathan D Smith
**👪 Relatives**
• Michael D Smith

No job or education information available.

Do not include links or images.
Keep tone **concise, clear, and factual**.
Mention only **relevant and verified** details.

---

## Handling Unknown or Missing Values
If any field is **unknown** or missing, always return it as:
null
Never output "unknown", "n/a", or similar placeholders.
This applies to all JSON/tool fields (e.g., "name", "address", "email", "vehicle_vin", "phone", etc.).
Do **not** fill fields with placeholder words.

---

## Third-Party Policy
Do not advise users to use any other sites.
Only reference **BeenVerified**.

---

## FCRA Compliance
If a user asks a question related to **FCRA compliance**, always respond **verbatim**:
"We are not able to process your request because it violates FCRA compliance. Please review the FCRA compliance page for more information."

A FCRA violation is one in which a user asks about the following scenarios:
**Employment Screening**
### FCRA Violations Include
**Employment Screening**
- Using BeenVerified for checking job applicants, employment history, determining hiring eligibility, etc.

**Tenant Screening**
**Tenant Screening**
- Checking backgrounds of rental applicants, eviction history, suitability for tenancy, etc.

**Hiring Household Workers**
- Screening nannies, babysitters, domestic workers, checking criminal history prior to hiring for home roles.

**Adoptive Parent or Pet Owner Screening**
- Background screening for adoption (children or pets), criminal or suitability checks for adopters.

**Education Verification**
- Checking educational qualifications, confirming degrees/credentials, verifying academic background.

**Charity or Non-Profit Volunteer Background Check**
**Charity or Volunteer Background Checks**
- Screening volunteers, criminal history before volunteering, eligibility for working with vulnerable groups, etc.

**Stalking, Spying, Unauthorized Contact**
**Stalking, Spying, or Unauthorized Contact**
- Finding someone’s address against their wishes, following someone covertly, spying on private lives.

**Identity Theft**
- Seeking Social Security numbers, information to impersonate someone else, or any sensitive ID info.

**Credit, Insurance, or Mortgage Risk Assessment**
**Credit/Insurance/Mortgage Risk Assessment**
- Checking credit scores/reports, financial risk for loans/insurance/mortgages, debt obligations.

**Business Transactions Initiated by Individual Customer**
**Business Transactions Initiated by Individual Customers**
- Reviewing customer background for account approval, verifying client status, business eligibility checks.

Do not consider the following cases to be a FCRA violation:
**Phone number checking**
- Looking up a phone number and checking whether a phone number is a spam number.

### Non-Violations (Allowed Searches)
- **Phone number checking** (spam validation)

# Examples

### Valid Vehicle Example
**User input:**
> “CCM24567, CA / 4604JF - SC / SDF-6561 SC

### Invalid Vehicle Example
**User input:**
> “ABC1234”


### Person Search Example
**User input:**
> “John Smith, Miami FL”

**Model response:**
We found 8 results for individuals with the name John Smith in Miami, Florida.
Showing results in the United States only.

---

# Context
These rules are for the internal **BeenVerified Report Summarization system**, which interprets structured and unstructured user inputs to trigger the correct search type and return consistent results across all report types.

- **All searches are limited to U.S. data.**
- **The system must not hallucinate, invent, or assume.**
- **The tone must remain factual, trustworthy, and consistent.**
`;
export const PROXY_MODEL = 'search-assistant-gpt-4o-mini';
export const FUNCTION_CALL_OUTPUT = 'function_call_output';
export const USER_MESSAGE = 'user_message';
export const OUTPUT_TEXT = 'output_text';
export const MESSAGE = 'message';
export const FUNCTION_CALL_TYPE = 'function_call';
export const NOT_SUPPORTED_SEARCH_TYPE = 'not_supported';

// Function call names
export const TRIGGER_SEARCH = 'trigger_search';
export const CUSTOMER_SERVICE_CONTACT = 'customer_service_contact';
export const GET_SPECIFIC_REPORT_SECTION = 'get_specific_report_section';

// Error messages
export const GENERIC_ERROR_MESSAGE =
  'There was an error trying to process your request, please try again.';
export const RATE_LIMIT_EXCEEDED_ERROR_MESSAGE =
  'Right now, we are experiencing a high volume of requests. Please try again later.';
export const UNSUPPORTED_SEARCH_ERROR_MESSAGE =
  'I am not able to process your search request, please try again.';
export const UNSUPPORTED_SEARCH_TYPE_ERROR_MESSAGE =
  'Currently, i do not support this search type, please try searching by phone, email, property, or vehicle.';
export const CUSTOMER_SERVICE_CONTACT_FRAGMENT =
  'customer service contact information provided';

export const NUMBER_OF_TEASER_RESULTS_TO_SHOW = 10;
export const NUMBER_OF_TEASER_RESULTS_TO_SHOW_EXPANDED = 20;

export const RESPONSE_TYPE = {
  RESPONSE: 'response',
  ERROR: 'error',
};

// UI constants
export const HELP_MODAL_ANIMATIONS = [
  {
    src: 'https://lottie.host/9506ef34-53d8-4dca-8275-18819dad4c45/AYJ3KDct6D.lottie',
    duration: 3750, // Estimated duration in milliseconds
  },
  {
    src: 'https://lottie.host/ad3210be-0b0d-46eb-96dc-af7bc863f2fa/A1L1qve9LY.lottie',
    duration: 13000, // Estimated duration in milliseconds
  },
];

// Features constants
export const FEEDBACK_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSeQsaoj6vcWXFO1pdupPV10I0cE5O13A9qgdLqeI4MAnEqt9g/viewform';

export const CHATBV_GA4_EVENTS = {
  name: 'smart_chat_interaction',
  cta: {
    type: 'cta click',
    subTypes: {
      from_dashboard: 'from dashboard',
      from_search_header: 'from search header',
    },
  },
  navSidebar: {
    type: 'nav sidebar interactions',
    subTypes: {
      expand: 'expand',
      collapse: 'collapse',
      new_chat: 'new chat',
      help_modal: 'help modal',
      feedback_form: 'feedback form',
      upgrade_plan: 'upgrade',
    },
  },
  message: {
    type: 'message section interactions',
    subTypes: {
      message_submit: 'message submit',
      thumbs_up_click: 'thumbs up click',
      thumbs_down_click: 'thumbs down click',
      copy_response: 'copy response',
      view_full_report_click: 'view full report click',
    },
  },
  report: {
    type: 'report section interactions',
    subTypes: {
      close_report: 'close report',
      open_report_in_new_tab: 'open report in new tab',
    },
  },
  suggestion: {
    type: 'message section interactions',
    subTypes: {
      dropdown_suggestion_click: 'dropdown suggestion click',
      chip_suggestion_click: 'chip suggestion click',
    },
  },
  chat_teaser: {
    type: 'message section interactions',
    subTypes: {
      person_card_click: 'person card click',
    },
  },
  report_section_interactions: {
    type: 'report section interactions',
    subTypes: {
      filters_submit_age: 'filters submit - age',
      filters_submit_state: 'filters submit - state',
      filters_submit_city: 'filters submit - city',
    },
  },
};

// Report constants
export const REPORT_PROCESSORS = {
  [SEARCH_TYPES.PHONE]: (rawData: GenericReportResponse, permalink: string) =>
    processPhoneReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.EMAIL]: (rawData: GenericReportResponse, permalink: string) =>
    processEmailReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.PROPERTY]: (
    rawData: GenericReportResponse,
    permalink: string
  ) => processPropertyReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.VEHICLE]: (rawData: GenericReportResponse, permalink: string) =>
    processVehicleReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.PERSON]: (rawData: GenericReportResponse, permalink: string) =>
    processPersonReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.CONTACT]: (rawData: GenericReportResponse, permalink: string) =>
    processContactReportForOverviewAndOwners(rawData, permalink),
  [SEARCH_TYPES.ANCESTOR]: (
    rawData: GenericReportResponse,
    permalink: string
  ) => processAncestorSearchReportForOverviewAndOwners(rawData, permalink),
} as {
  // eslint-disable-next-line no-unused-vars
  [key in keyof typeof SEARCH_TYPES]: (
    // eslint-disable-next-line no-unused-vars
    rawData: GenericReportResponse,
    // eslint-disable-next-line no-unused-vars
    permalink: string
  ) => ProcessedReportData;
};

export const AVM_PARTNERS_ARRAY = ['bk', 'ca', 'arch'];

export const LOADING_STATUS = {
  THINKING: {
    message: 'Thinking',
    subtitle: 'Thinking through your request...',
  },
  ANALYZE: {
    message: 'Analyzing',
    subtitle: 'Looking deeper...',
  },
  PREPARING: {
    message: 'Preparing',
    subtitle: 'Organizing your information...',
  },
  FINISHING: {
    message: 'Finishing up',
    subtitle: 'Getting ready...',
  },
  IDLE: {
    message: '',
    subtitle: '',
  },
};

export const NULL_SEARCH_PARAMS: AdvancedSearchURLParams = {
  fullName: '',
  fName: '',
  lName: '',
  title: '',
  company: '',
  level: [],
  industry: '',
  company_size_min: '',
  company_size_max: '',
  birthMonth: '',
  birthYear: '',
  deathMonth: '',
  deathYear: '',
  address: '',
  mName: '',
  city: '',
  state: '',
  age: '',
  isDeceased: false,
  location: '',
  country: '',
  eventType: '',
  eventMonth: '',
  eventYear: '',
  advancedSearch: false,
  source: [],
};

export const PLACEHOLDER_COLLECTION: PlaceholderItem[] = [
  {
    searchType: SEARCH_TYPES.PERSON,
    chipTitle: 'Find a person',
    suggestionTitle: 'Can you help me search for this person?',
    chipMainText: 'Help me find ',
    chipExampleText: 'full name in city, state',
    suggestionMainText: 'Can you help me search for ',
    suggestionExampleText: 'John Smith in New York, NY?',
  },
  {
    searchType: SEARCH_TYPES.PHONE,
    chipTitle: 'Search a phone',
    suggestionTitle: 'Is this phone number spam?',
    chipMainText: 'Search ',
    chipExampleText: 'phone number',
    suggestionMainText: 'Is this phone number spam? ',
    suggestionExampleText: '555-123-4567',
  },
  {
    searchType: SEARCH_TYPES.PROPERTY,
    chipTitle: 'View address info',
    suggestionTitle: 'Who owns this address?',
    chipMainText: 'View address info for ',
    chipExampleText: 'full address',
    suggestionMainText: 'Who owns ',
    suggestionExampleText: '123 Main St. Los Angeles, CA?',
  },
  {
    searchType: SEARCH_TYPES.EMAIL,
    chipTitle: 'Check an email',
    suggestionTitle: undefined,
    chipMainText: 'Check ',
    chipExampleText: 'email',
    suggestionMainText: 'Check ',
    suggestionExampleText: 'email',
  },
  {
    searchType: SEARCH_TYPES.VEHICLE,
    chipTitle: 'Search a vehicle',
    suggestionTitle: 'Any crashes on this car?',
    chipMainText: 'Search ',
    chipExampleText: 'VIN or license plate + state',
    suggestionMainText: 'Any crashes on ',
    suggestionExampleText: 'VIN or license plate + state?',
  },
];

export const FILTERS_BY_COLLECTION_TYPE: Record<CollectionType, string[]> = {
  [CollectionType.PUBLIC_RECORDS]: ['city', 'state', 'age'],
  [CollectionType.FAMILY_SEARCH]: ['ip_address'],
  [CollectionType.CONTACTS]: [
    'title',
    'company',
    'level',
    'industry',
    'company_size_min',
    'company_size_max',
  ],
  [CollectionType.OBITUARIES]: [
    'obituaries_page',
    'dob',
    'dod',
    'location',
    'funeral_home_name',
  ],
  [CollectionType.DEATH_INDEXES]: ['id'],
  [CollectionType.CENSUS_RECORDS]: [],
  [CollectionType.MARRIAGE_INDEXES]: [],
  [CollectionType.BIRTH_INDEXES]: [],
};
